// **The sandbox's playback loop** — the composition root's half of the playhead.
//
// `sandbox-coordinator.ts` owns *where* the playhead lands and *when playback stops*; this owns
// *when a tick happens*, which is a wall-clock decision and therefore explicitly not the reducer's
// (design **T2**) and not the screen's (**T1**: `SandboxScreen.host.test.tsx` asserts the screen's
// source contains no `requestAnimationFrame`). It is the same split the run-progress poll makes one
// file over in `RoundsController.tsx`.
//
// The shape is deliberately `RoundsReview.tsx`'s, not a second invention: schedule through an
// injected {@link FrameScheduler}, read time through an injected {@link Clock}, re-arm from
// `clock.now()` on every (re)start so a pause accrues no time debt, and cancel on cleanup. Both
// seams are why "1× advances 15 ticks per second" is arithmetic in a test rather than a wait.
//
// ## The remainder is the whole point
//
// The playhead is an **integer tick** everywhere it is read — `detailAt`, the mm:ss clock, the chip
// counts — so the reducer's `playhead-advanced` takes whole ticks only. But one 60 fps frame at a
// 15 Hz trace is a *quarter* of a tick: a loop that rounded each frame in isolation would advance
// zero ticks, forever, and present as a transport that plays but never moves. So the fractional
// part is carried here, across frames, and only whole ticks are ever dispatched.
//
// This module lives beside `RoundsController.tsx` rather than inside it for one reason: a hook with
// an injected scheduler and clock is testable on its own, and a `useEffect` buried in a 900-line
// composition root is testable only through a whole fake deployment.

import { useEffect } from 'react';
import type { Clock, FrameScheduler } from '../../canvas/frame-scheduler';
import type { ReviewTrace } from '../../net/rounds-review-source';
import type { SandboxSpeed } from '../../ui/rounds/run-submit-model';
import type { SandboxCoordinator } from '../../ui/rounds/sandbox-coordinator';

const MS_PER_SECOND = 1000;

export interface PlayheadLoopInput {
  /** The sandbox coordinator, or `null` before the sandbox has opened. */
  coordinator: SandboxCoordinator | null;
  /** The loaded run's trace, or `null` when there is nothing to play. Its `tickRateHz` is the
   * conversion from wall clock to ticks, so the loop reads the run's own rate rather than a seed. */
  trace: ReviewTrace | null;
  /** The coordinator's own `playback.playing`. The loop follows it; it never sets it. */
  playing: boolean;
  /** The selected step from the bar's 1× / 4× / 16× ladder (**D14**). */
  speed: SandboxSpeed;
  scheduler: FrameScheduler;
  clock: Clock;
}

/**
 * Runs an animation-frame loop that dispatches `playhead-advanced` while playback is on.
 *
 * Stops when playback stops, when the trace goes, and on unmount — cancellation is the effect's
 * cleanup, so there is no path where a frame outlives the screen. (Even if one did, the reducer
 * discards every event after `cancelled`; this is belt and braces on a loop that must not leak.)
 */
export function usePlayheadLoop({
  coordinator,
  trace,
  playing,
  speed,
  scheduler,
  clock,
}: PlayheadLoopInput): void {
  useEffect(() => {
    if (coordinator === null || trace === null || !playing) {
      return undefined;
    }
    let cancelled = false;
    let handle = 0;
    let last = clock.now();
    /** Ticks accrued but not yet worth dispatching — see the header. */
    let carry = 0;

    const frame = (): void => {
      if (cancelled) {
        return;
      }
      const now = clock.now();
      // `Math.max(0, …)` guards a clock that goes backwards (a `performance.now()` on a suspended
      // tab, a test that rewinds): a negative delta would drag the playhead backwards mid-play.
      const deltaMs = Math.max(0, now - last);
      last = now;
      carry += (deltaMs / MS_PER_SECOND) * trace.tickRateHz * speed;
      const whole = Math.floor(carry);
      if (whole >= 1) {
        carry -= whole;
        coordinator.dispatch({ kind: 'playhead-advanced', ticks: whole });
      }
      handle = scheduler.schedule(frame);
    };

    handle = scheduler.schedule(frame);
    return () => {
      cancelled = true;
      scheduler.cancel(handle);
    };
  }, [coordinator, trace, playing, speed, scheduler, clock]);
}
